import { corsUrl, loadBitmap, type AssetCatalog } from './assets';

const ATLAS = 'atlas/images/bomb/crater/bomb_crater';

/** Laranja do miolo (RGB aproximado): o miolo e as bordas suavizadas dele contra o preto têm esta mesma proporção de cor. */
const FILL_RGB = [255, 123, 35] as const;

/** Pixel do miolo laranja chapado (o preenchimento do buraco na arte da borda). */
const isFill = (r: number, g: number, b: number): boolean => r >= 200 && g >= 80 && g <= 150 && b < 80;
/** Pixel com a proporção de cor do miolo, claro ou escuro (inclui a beirada suavizada contra o preto). */
const hasFillHue = (r: number, g: number, b: number): boolean => r >= 40 && Math.abs(g / r - FILL_RGB[1] / FILL_RGB[0]) <= 0.1 && b / r <= 0.22;

/**
 * Tira o miolo laranja da borda, em RGBA (altera o vetor). A arte da borda traz o preenchimento do buraco como um miolo laranja chapado. O legado o
 * apaga ao multiplicar a borda pelo terreno que sobrou, mas em várias bombas o miolo passa do buraco e sobra um anel laranja sobre o terreno.
 * Pixel do miolo vira transparente. Pixel de matiz laranja encostado no miolo (a beirada suavizada contra o preto) perde a parte laranja e fica
 * preto com o alfa que sobra. O resto não muda: as rachaduras escuras e os detalhes laranja ou amarelos longe do miolo (brilho, pedras) ficam.
 */
export function removeOrangeFill(rgba: Uint8ClampedArray, width: number, height: number): void {
  const fillAt = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    const i = (y * width + x) * 4;
    return rgba[i + 3]! > 40 && isFill(rgba[i]!, rgba[i + 1]!, rgba[i + 2]!);
  };
  const clear: number[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = rgba[i]!;
      if (!hasFillHue(r, rgba[i + 1]!, rgba[i + 2]!)) continue;
      let touches = false;
      for (let dy = -1; dy <= 1 && !touches; dy++) {
        for (let dx = -1; dx <= 1 && !touches; dx++) touches = fillAt(x + dx, y + dy);
      }
      if (touches) clear.push(i);
    }
  }
  // Decide com a imagem original e só então altera: o pixel limpo não faz vizinho virar miolo.
  for (const i of clear) {
    const fill = Math.min(1, rgba[i]! / FILL_RGB[0]);
    rgba[i] = 0;
    rgba[i + 1] = 0;
    rgba[i + 2] = 0;
    rgba[i + 3] = Math.round(rgba[i + 3]! * (1 - fill));
  }
}

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number };
  sourceSize?: { w: number; h: number };
}

/**
 * Borda da cratera (`craterBrink.png` do legado): a faixa queimada que fica no contorno do buraco. O legado desenha a borda só onde o
 * terreno continua (a borda é multiplicada pelo alfa do terreno depois do buraco aberto) e a pinta sobre o terreno.
 * As bordas vêm todas num atlas só; bomba sem borda no atlas só abre o buraco.
 */
export class CraterBrinks {
  private frames = new Map<number, RawFrame>();
  private sheet: ImageBitmap | null = null;
  private ready: Promise<void> | null = null;

  constructor(private readonly assets: AssetCatalog) {}

  /** Baixa o atlas uma vez. Sem atlas no manifest ou com falha de rede, `has` devolve sempre falso. */
  load(): Promise<void> {
    this.ready ??= this.fetchAtlas().catch(() => undefined);
    return this.ready;
  }

  private async fetchAtlas(): Promise<void> {
    if (!this.assets.has(`${ATLAS}.json`) || !this.assets.has(`${ATLAS}.webp`)) return;
    const response = await fetch(corsUrl(this.assets.url(`${ATLAS}.json`)));
    if (!response.ok) return;
    const json = (await response.json()) as { frames: Record<string, RawFrame> };
    for (const [name, frame] of Object.entries(json.frames)) {
      const match = /^([0-9]+)\/craterbrink$/i.exec(name);
      if (match) this.frames.set(Number(match[1]), frame);
    }
    this.sheet = await loadBitmap(this.assets.url(`${ATLAS}.webp`));
  }

  has(ballId: number): boolean {
    return this.sheet !== null && this.frames.has(ballId);
  }

  /** Imagem da borda no tamanho original (com a margem transparente), ou `null` se a bomba não tem borda. */
  image(ballId: number): HTMLCanvasElement | null {
    const f = this.frames.get(ballId);
    if (!f || !this.sheet) return null;
    const canvas = document.createElement('canvas');
    canvas.width = f.sourceSize?.w ?? f.frame.w;
    canvas.height = f.sourceSize?.h ?? f.frame.h;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(this.sheet, f.frame.x, f.frame.y, f.frame.w, f.frame.h, f.spriteSourceSize?.x ?? 0, f.spriteSourceSize?.y ?? 0, f.frame.w, f.frame.h);
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    removeOrangeFill(pixels.data, canvas.width, canvas.height);
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }
}
